import { DateTime } from "@bps/date-time";
import { SideRailMenuItem } from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { FormTemplateTypeCode } from "@libs/gateways/forms/FormsGateway.dtos.ts";
import {
  AddPatientDto,
  ContactStatus,
  DvaCardColour
} from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { Labels } from "@modules/practice/screens/shared-components/types/labels.enums.types.ts";
import { PatientCardIds } from "@modules/practice/screens/shared-components/types/patient-card-ids.enum.ts";
import { toAddressDto } from "@modules/practice/screens/shared-components/utils/contact.utils.ts";
import { BookingStore } from "@stores/booking/BookingStore.ts";
import { CommsStore } from "@stores/comms/CommsStore.ts";
import { ContactPreferences } from "@stores/comms/models/ContactPreferences.tsx";
import { Contact, toEntitlements } from "@stores/practice/models/Contact.ts";
import { PracticeStore } from "@stores/practice/PracticeStore.ts";
import { RootStore } from "@stores/root/RootStore.ts";

import { PatientDemographicHelper } from "../../context/PatientDemographicHelper.ts";
import { PatientEditFormValues as PatientFormValues } from "../../shared-components/edit/PatientEditFormValues.tsx";
import {
  getCommTypePreferences,
  toCommunicationDto,
  toExternalProviderDto,
  toInternalProviderDto,
  toRelationshipDto,
  toRelationshipDtoFromAccountHoldersField,
  toRelationshipDtoFromEmployerField
} from "../../shared-components/edit/utils.tsx";

const matchContactWithCalendarEvent = async (
  patientId: string,
  booking: BookingStore
) => {
  const onCreateContact = booking.ui.patientMatchInfo?.onCreateContact;
  onCreateContact && (await onCreateContact(patientId));
};

const submitPicture = async (
  contact: Contact,
  fileStagingId: string,
  practice: PracticeStore
) => {
  await practice.updateProfilePicture({
    entityId: contact.id,
    fileStagingId
  });
};

const addEditContactPreferences = async (
  options: {
    updatedPatient: Contact;
    contactPreferences: ContactPreferences | undefined;
  },
  values: PatientFormValues,
  comms: CommsStore
) => {
  const commTypePreferences = getCommTypePreferences(values);
  const { contactPreferences, updatedPatient } = options;
  if (contactPreferences)
    await comms.patchContactPreferences({
      commTypePreferences,
      id: updatedPatient.id,
      eTag: contactPreferences.eTag
    });
  else
    await comms.addContactPreferences({
      commTypePreferences,
      id: updatedPatient.id
    });
};

const createPatientNotice = async (
  updatedPatient: Contact,
  practice: PracticeStore
) => {
  const patientNotices = await practice.getPatientNotices(updatedPatient.id);

  const genderDiverseNotice = patientNotices.find(x => x.type === "GD");
  if (!genderDiverseNotice) {
    const notice = {
      patientId: updatedPatient.id,
      priority: "MED",
      type: "GD",
      comment: "",
      isAdmin: false,
      isClinical: true
    };

    await practice.addPatientNotice(notice);
  }
};

export const onSubmit = async (
  contact: Contact | undefined,
  formOptions: { values: PatientFormValues; initialValues: PatientFormValues },
  options: {
    root: RootStore;
    helper: PatientDemographicHelper;
    contactPreferences: ContactPreferences | undefined;
  }
) => {
  const { initialValues, values } = formOptions;

  const { root, helper, contactPreferences } = options;
  const {
    employers,
    relationships,
    accountHolders,
    status,
    firstName,
    lastName,
    middleName,
    title,
    nickName,
    birthDate,
    dateOfDeath,
    medicare,
    healthInsurance: healthFund,
    dva,
    csc,
    nhi,
    profilePictureUrl,
    appointmentReminderType,
    appointmentReminderValue,
    appointmentConfirmationType,
    appointmentConfirmationValue,
    invoiceCommunicationType,
    invoiceCommunicationValue,
    emailForm,
    smsForm,
    tempFormAddressValue,
    tempFormChannelType,
    ...restValues
  } = values;

  const {
    userExperience,
    notification,
    forms,
    booking,
    comms,
    practice,
    practice: { addPatient, updatePatient, deleteProfilePicture }
  } = root;

  if (dva && dva.cardColor !== DvaCardColour.White) {
    dva.disability = undefined;
  }

  const healthFundNew =
    healthFund?.number && healthFund.fund
      ? {
          fund: healthFund.fund,
          number: healthFund.number,
          expiry: DateTime.jsDateToISODate(healthFund.expiry)
        }
      : undefined;

  const medicareNew =
    medicare?.number && medicare.irnNumber
      ? {
          number: medicare.number,
          irnNumber: medicare.irnNumber,
          expiryMonth: medicare.expiryMonth,
          expiryYear: medicare.expiryYear
        }
      : undefined;

  const dvaNew = dva?.number
    ? {
        number: dva.number,
        cardColor: dva.cardColor,
        disability: dva.disability
      }
    : undefined;

  const cscNew = csc?.number
    ? {
        number: csc.number,
        startDate: DateTime.jsDateToISODate(csc.startDate),
        expiry: DateTime.jsDateToISODate(csc.expiry)
      }
    : undefined;

  const nhiNew = nhi
    ? {
        number: nhi.toUpperCase()
      }
    : undefined;

  const baseRequest: AddPatientDto = {
    ...restValues,
    fullName: {
      firstName,
      lastName,
      nickName,
      middleName,
      title
    },
    contactStatus: status,
    communications: toCommunicationDto(values.communications),
    addresses: toAddressDto(values.addresses),
    patientEntitlements: toEntitlements({
      med: medicareNew,
      hf: healthFundNew,
      dva: dvaNew,
      nhi: nhiNew,
      csc: cscNew
    }),
    birthday: birthDate ? DateTime.jsDateToISODate(birthDate) : undefined,
    dateOfDeath: dateOfDeath
      ? DateTime.jsDateToISODate(dateOfDeath)
      : undefined,
    internalProvider: toInternalProviderDto(values.internalProvider),
    externalProvider: toExternalProviderDto(values.externalProvider),
    interpreterLanguage: values.interpreterLanguage
      ? values.interpreterLanguage
      : undefined
  };

  baseRequest.relationships = toRelationshipDto(
    [...relationships, ...accountHolders],
    contact?.relationships
  );

  if (employers && employers.length > 0) {
    baseRequest.relationships.push(
      ...toRelationshipDtoFromEmployerField(employers)
    );
  }

  if (accountHolders && accountHolders.length > 0) {
    baseRequest.relationships.push(
      ...toRelationshipDtoFromAccountHoldersField(accountHolders)
    );
  }

  if (contact) {
    userExperience.upsertPatientSetting({
      patientId: contact.id,
      pronounMessage: helper.displayGenderMessageBar
    });

    if (!!initialValues.profilePictureUrl && !values.profilePictureUrl) {
      try {
        await deleteProfilePicture(contact.id);
      } catch (error) {
        notification.error("Error occurred while removing profile photo");
      }
    }
  }

  let updatedPatient;
  if (!values.id) {
    updatedPatient = await addPatient({
      ...baseRequest,
      contactStatus: ContactStatus.Active
    });
  } else {
    updatedPatient = await updatePatient({
      id: values.id,
      ...baseRequest
    });
  }

  if (updatedPatient) {
    await addEditContactPreferences(
      { updatedPatient, contactPreferences },
      values,
      comms
    );
    if (
      updatedPatient.sex &&
      updatedPatient.gender &&
      updatedPatient.sex.toString() !== updatedPatient.gender.toString()
    ) {
      await createPatientNotice(updatedPatient, practice);
    }
  }

  if (helper.profilePictureStagingId && updatedPatient) {
    // stores picture and fetches contact with new picture url
    await submitPicture(
      updatedPatient,
      helper.profilePictureStagingId,
      practice
    );
  }
  if (
    (tempFormAddressValue && tempFormChannelType) ||
    typeof smsForm === "number" ||
    typeof emailForm === "number"
  ) {
    try {
      const context: Record<string, string> = {
        PatientId: updatedPatient.id
      };

      const formTemplate = await forms.getSingleTemplateByCode(
        FormTemplateTypeCode.patientDemographic
      );
      if (formTemplate) {
        await forms.deployForm({
          context,
          formTemplate,
          addressValue: values.tempFormAddressValue,
          channelCode: values.tempFormChannelType
        });
      }
    } catch (error) {
      notification.error("Error occurred while deploying form");
    }
  }
  await matchContactWithCalendarEvent(updatedPatient.id, booking);
};

export const getMenuItems = (
  commsPrefText: string
): (SideRailMenuItem & {
  permissions?: Permission[];
})[] => {
  return [
    {
      text: Labels.personalInfo,
      id: PatientCardIds.patientHeader
    },
    {
      text: Labels.contactMethods,
      id: PatientCardIds.contactMethods
    },
    {
      text: commsPrefText,
      id: PatientCardIds.prefCons
    },
    {
      text: Labels.account,
      id: PatientCardIds.patientAccount,
      permissions: [Permission.AccountHistoryAllowed]
    },
    {
      text: Labels.relationships,
      id: PatientCardIds.contactRelationships
    },
    {
      text: Labels.cardsEntitlements,
      id: PatientCardIds.patientEntitlements
    },
    {
      text: Labels.healthProviders,
      id: PatientCardIds.healthProviders
    },
    {
      text: Labels.employers,
      id: PatientCardIds.employers
    },
    {
      text: Labels.profile,
      id: PatientCardIds.patientProfile
    }
  ];
};
